import { useEffect } from 'react';

/** Campos donde el usuario esta escribiendo y un atajo simple no debe actuar. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  );
}

function modifiersMatch(event: KeyboardEvent, needsModifier: boolean): boolean {
  const hasCtrlOrMeta = event.ctrlKey || event.metaKey;
  if (needsModifier) return hasCtrlOrMeta;
  return !hasCtrlOrMeta && !event.altKey;
}

/**
 * Registra un atajo global de teclado.
 *
 * Un atajo sin modificador se ignora mientras el foco esta en un campo de
 * texto: un operario tecleando un SKU que contenga esa letra no debe disparar
 * la accion. Los atajos con modificador y Escape si actuan siempre, porque su
 * proposito es precisamente poder salir o invocar algo desde donde sea.
 */
export function useHotkey(
  key: string,
  handler: () => void,
  options: { readonly ctrlOrMeta?: boolean } = {},
): void {
  const needsModifier = options.ctrlOrMeta === true;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() !== key.toLowerCase()) return;
      if (!modifiersMatch(event, needsModifier)) return;

      const blockedWhileTyping = !needsModifier && key !== 'Escape';
      if (blockedWhileTyping && isTypingTarget(event.target)) return;

      event.preventDefault();
      handler();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [key, handler, needsModifier]);
}
